/**
 * Notification router.
 *
 * Drives the in-app notification bell + inbox. Athletes (and every
 * other authenticated user) see only their own notifications — the
 * Notification model's ZenStack policy enforces this at the row level
 * (`@@allow('read,update', auth() == user)`), so all reads here go
 * through the `context.db` enhanced client and naturally row-filter.
 *
 * Notifications are CREATED by other routers (compliance.recordDecision,
 * eligibility.reject, organization.invite, etc.) — this router never
 * exposes a create path to the caller.
 *
 * @package @app/api
 */

import { ORPCError } from '@orpc/server';
import { z } from 'zod';
import { protectedProcedure } from '../middleware/auth';

export const notificationRouter = {
  /**
   * Recent notifications for the signed-in user. Capped at 50 — the
   * bell dropdown shows the most recent, and anything older lives in
   * the full inbox at /notifications (paged separately when we add it).
   */
  list: protectedProcedure
    .input(
      z
        .object({
          limit: z.number().int().positive().max(50).default(20),
          unreadOnly: z.boolean().default(false),
        })
        // `.default(() => ({...}))` — Zod 4's stricter type inference
        // rejects `.default({})` here because it computes the schema's
        // OUTPUT type (post-default) and demands the default match it.
        // The thunk form sidesteps that by deferring evaluation.
        .default(() => ({ limit: 20, unreadOnly: false })),
    )
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const rows = await db.notification.findMany({
        where: {
          userId: session.user.id,
          ...(input.unreadOnly ? { read: false } : {}),
        },
        orderBy: { createdAt: 'desc' },
        take: input.limit,
        select: {
          id: true,
          title: true,
          body: true,
          type: true,
          read: true,
          readAt: true,
          actionUrl: true,
          createdAt: true,
        },
      });
      // biome-ignore lint/suspicious/noExplicitAny: prisma row
      return rows.map((r: any) => ({
        ...r,
        readAt: r.readAt?.toISOString() ?? null,
        createdAt: r.createdAt.toISOString(),
      }));
    }),

  /**
   * Unread count. Polled by the bell badge — cheap and not row-bound,
   * so we keep it on its own query key for tight invalidation.
   */
  unreadCount: protectedProcedure.handler(async ({ context }) => {
    const { db, session } = context;
    const count = await db.notification.count({
      where: { userId: session.user.id, read: false },
    });
    return { count };
  }),

  /**
   * Mark a single notification as read. Idempotent — re-marking a read
   * notification is a no-op success.
   */
  markRead: protectedProcedure
    .input(z.object({ id: z.string() }))
    .handler(async ({ context, input }) => {
      const { db, session } = context;
      const existing = await db.notification.findFirst({
        where: { id: input.id, userId: session.user.id },
        select: { id: true, read: true },
      });
      if (!existing) {
        throw new ORPCError('NOT_FOUND', { message: 'Notification not found' });
      }
      if (existing.read) return { ok: true, alreadyRead: true as const };
      await db.notification.update({
        where: { id: existing.id },
        data: { read: true, readAt: new Date() },
      });
      return { ok: true, alreadyRead: false as const };
    }),

  /**
   * Bulk mark every unread notification as read. Used by the bell's
   * "Mark all read" footer button so the user can clear the badge in
   * one click without opening each row.
   */
  markAllRead: protectedProcedure.handler(async ({ context }) => {
    const { db, session } = context;
    const result = await db.notification.updateMany({
      where: { userId: session.user.id, read: false },
      data: { read: true, readAt: new Date() },
    });
    return { ok: true, marked: result.count };
  }),
};
